import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CalendarClock,
  ChevronRight,
  Filter,
  Flame,
  Loader2,
  MessageCircle,
  Pencil,
  Phone,
  Plus,
  Search,
  Share2,
  ShieldCheck,
  ArrowRightLeft,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import AttemptCounter from "@/components/AttemptCounter";
import ContactPolicyBadge, { policyFor } from "@/components/ContactPolicyBadge";
import LeadContactActions from "@/components/LeadContactActions";
import { useWhatsAppInbox } from "@/contexts/WhatsAppInboxContext";
import VoiceNoteRecorder from "@/components/VoiceNoteRecorder";
import { NotesTimeline } from "@/components/NotesTimeline";
import WhatsAppConversation from "@/components/WhatsAppConversation";
import ReferLeadsDialog from "@/components/ReferLeadsDialog";
import AddLeadDialog from "@/components/AddLeadDialog";
import CloseReasonDialog from "@/components/CloseReasonDialog";
import EditLeadDetailsModal from "@/components/EditLeadDetailsModal";
import TransferLeadDialog from "@/components/TransferLeadDialog";
import LeadDocumentsPanel from "@/components/LeadDocumentsPanel";
import LeadPassesPanel from "@/components/LeadPassesPanel";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function parseAdCopy(lead) {
  const structured = (lead?.ad_headline || "").trim();
  if (structured) return structured;
  const notes = lead?.notes || "";
  for (const prefix of ["Ad copy:", "From ad:"]) {
    const idx = notes.indexOf(prefix);
    if (idx === -1) continue;
    let rest = notes.slice(idx + prefix.length);
    for (const sep of [" | ", "\n", "\r"]) {
      const cut = rest.indexOf(sep);
      if (cut !== -1) rest = rest.slice(0, cut);
    }
    const val = rest.trim();
    if (val) return val;
  }
  return null;
}

const DATE_PRESETS = [
  { v: "all", l: "Any time" },
  { v: "today", l: "Today" },
  { v: "7d", l: "Last 7 days" },
  { v: "30d", l: "Last 30 days" },
  { v: "custom", l: "Custom range" },
];

const TEMP_OPTIONS = ["Hot", "Warm", "Cool", "Cold"];

const TEMP_DOT = {
  Hot: "bg-rose-500",
  Warm: "bg-amber-500",
  Cool: "bg-sky-500",
  Cold: "bg-slate-400",
};

const GENERAL_STATUS_PILL = {
  Untouched: "bg-slate-100 text-slate-700",
  "Not Connected": "bg-amber-100 text-amber-800",
  Cold: "bg-sky-100 text-sky-800",
  Warm: "bg-orange-100 text-orange-800",
  "Future Lead": "bg-purple-100 text-purple-700",
  Closed: "bg-rose-100 text-rose-700",
  Referred: "bg-violet-100 text-violet-800",
};

const COUNSELING_STATUS_PILL = {
  New: "bg-blue-100 text-blue-800",
  "Docs Received": "bg-teal-100 text-teal-800",
  Visited: "bg-emerald-100 text-emerald-800",
  "Zoom Done": "bg-violet-100 text-violet-800",
  "Options Sent": "bg-indigo-100 text-indigo-800",
  Enrolled: "bg-green-100 text-green-800",
  Spoke: "bg-amber-100 text-amber-800",
  "Future Lead": "bg-purple-100 text-purple-700",
  Closed: "bg-rose-100 text-rose-700",
};

export const GENERAL_CONFIG = {
  title: "Leads",
  pipeline: "general",
  statusField: "status",
  defaultStatus: "Untouched",
  stages: ["Untouched", "Not Connected", "Cold", "Warm", "Future Lead", "Closed", "Referred"],
  chipStages: ["Untouched", "Not Connected", "Cold", "Warm", "Future Lead", "Referred", "Closed"],
  showRefByMe: true,
  referToCounseling: true,
  statusPill: GENERAL_STATUS_PILL,
};

export const COUNSELING_CONFIG = {
  title: "Counselings",
  pipeline: "counseling",
  statusField: "counseling_status",
  defaultStatus: "New",
  stages: [
    "New", "Docs Received", "Visited", "Zoom Done",
    "Options Sent", "Enrolled", "Spoke", "Future Lead", "Closed",
  ],
  chipStages: [
    "New", "Docs Received", "Visited", "Zoom Done",
    "Options Sent", "Enrolled", "Spoke", "Future Lead", "Closed",
  ],
  showRefByMe: false,
  referToCounseling: false,
  statusPill: COUNSELING_STATUS_PILL,
};

function waUrl(raw, name) {
  const digits = String(raw || "").replace(/\D/g, "");
  if (!digits) return null;
  const withCc = digits.length === 10 ? `91${digits}` : digits;
  const first = (name || "there").split(" ")[0];
  return `https://wa.me/${withCc}?text=${encodeURIComponent(
    `Hi ${first}, reaching out from MedAdmits. Free to talk?`,
  )}`;
}

const SORTS = [
  { v: "newest", l: "Newest first" },
  { v: "score", l: "Lead score" },
  { v: "name", l: "Name (A–Z)" },
];

export default function LeadsPage({ config = GENERAL_CONFIG }) {
  const sf = config.statusField;
  const { user } = useAuth();
  const { byLead: waByLead } = useWhatsAppInbox();
  const userId = user?.id;
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState("all");
  const [q, setQ] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [fAdCopy, setFAdCopy] = useState("all");
  const [fCounsellor, setFCounsellor] = useState("all");
  const [fTemp, setFTemp] = useState("all");
  const [fSource, setFSource] = useState("all");
  const [fSla, setFSla] = useState("all");
  const [fDatePreset, setFDatePreset] = useState("all");
  const [fDateFrom, setFDateFrom] = useState("");
  const [fDateTo, setFDateTo] = useState("");
  const [sort, setSort] = useState("newest");
  const [selected, setSelected] = useState({});
  const [referOpen, setReferOpen] = useState(false);
  const [referIds, setReferIds] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [pendingClose, setPendingClose] = useState(null); // {id}
  const [editLead, setEditLead] = useState(null); // lead being edited (name/phone)
  const [searchHits, setSearchHits] = useState([]); // tenant-wide search matches

  // Schedule-call dialog (reused pattern from the main Dashboard)
  const [scheduleLead, setScheduleLead] = useState(null);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [scheduleNote, setScheduleNote] = useState("");
  const [scheduleSaving, setScheduleSaving] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/leads", {
        params: {
          pipeline: config.pipeline,
          sort: sort === "score" ? "score" : undefined,
        },
      });
      setLeads(data?.leads || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [sort, config.pipeline]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  // Tenant-wide search: surface leads owned by other members (read-only) so
  // members know a lead already exists in the consultancy. Debounced.
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setSearchHits([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/admin/leads/search", { params: { q: term } });
        if (!cancelled) setSearchHits(data?.leads || []);
      } catch {
        if (!cancelled) setSearchHits([]);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q]);

  // Open the lead drawer when the notification bell deep-links with ?focus=<id>
  useEffect(() => {
    const focusId = searchParams.get("focus");
    if (!focusId || loading) return;
    const target = leads.find((l) => l.id === focusId);
    if (target) {
      setSelectedLead(target);
      const next = new URLSearchParams(searchParams);
      next.delete("focus");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, leads, loading, setSearchParams]);

  const counts = useMemo(() => {
    const c = { all: leads.length, refbyme: 0 };
    config.chipStages.forEach((s) => (c[s] = 0));
    leads.forEach((l) => {
      const st = l[sf];
      if (st && c[st] !== undefined) c[st] += 1;
      if (l.referred_by && l.referred_by === userId) c.refbyme += 1;
    });
    return c;
  }, [leads, userId, sf, config.chipStages]);

  const filtered = useMemo(() => {
    let rows = [...leads];
    if (stage === "refbyme") {
      rows = rows.filter((l) => l.referred_by === userId);
    } else if (stage !== "all") {
      rows = rows.filter((l) => l[sf] === stage);
    }
    if (q.trim()) {
      const s = q.toLowerCase();
      rows = rows.filter(
        (l) =>
          (l.name || "").toLowerCase().includes(s) ||
          (l.phone || "").toLowerCase().includes(s) ||
          (l.email || "").toLowerCase().includes(s),
      );
    }
    if (fAdCopy !== "all") rows = rows.filter((l) => (parseAdCopy(l) || "—") === fAdCopy);
    if (fCounsellor !== "all") {
      rows = fCounsellor === "__unassigned__"
        ? rows.filter((l) => !l.assigned_to)
        : rows.filter((l) => l.assigned_to === fCounsellor);
    }
    if (fTemp !== "all") rows = rows.filter((l) => l.lead_temperature === fTemp);
    if (fSource !== "all") rows = rows.filter((l) => (l.source_channel || "—") === fSource);
    if (fSla !== "all") {
      rows = rows.filter((l) => {
        const p = policyFor(l);
        if (fSla === "overdue") return p.kind === "overdue";
        if (fSla === "ontrack") return p.kind !== "overdue";
        return true;
      });
    }
    if (fDatePreset !== "all") {
      const now = Date.now();
      let from = null, to = null;
      if (fDatePreset === "today") {
        const d = new Date(); d.setHours(0, 0, 0, 0); from = d.getTime();
      } else if (fDatePreset === "7d") {
        from = now - 7 * 86400000;
      } else if (fDatePreset === "30d") {
        from = now - 30 * 86400000;
      } else if (fDatePreset === "custom") {
        if (fDateFrom) from = new Date(`${fDateFrom}T00:00:00`).getTime();
        if (fDateTo) to = new Date(`${fDateTo}T23:59:59`).getTime();
      }
      rows = rows.filter((l) => {
        const t = l.created_at ? new Date(l.created_at).getTime() : null;
        if (t === null) return false;
        if (from !== null && t < from) return false;
        if (to !== null && t > to) return false;
        return true;
      });
    }
    if (sort === "name") {
      rows.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    } else if (sort === "score") {
      rows.sort((a, b) => (b.lead_score || 0) - (a.lead_score || 0));
    }
    // Append tenant-wide matches owned by other members (read-only) when
    // actively searching, so members can see the lead already exists.
    if (q.trim().length >= 2 && searchHits.length) {
      const ids = new Set(rows.map((r) => r.id));
      const extra = searchHits.filter((h) => h.out_of_scope && !ids.has(h.id));
      rows = [...rows, ...extra];
    }
    return rows;
  }, [leads, stage, q, sort, userId, sf, fAdCopy, fCounsellor, fTemp, fSource, fSla, fDatePreset, fDateFrom, fDateTo, searchHits]);

  const adCopyOptions = useMemo(() => {
    const set = new Set();
    leads.forEach((l) => { const a = parseAdCopy(l); if (a) set.add(a); });
    return Array.from(set).sort();
  }, [leads]);
  const counsellorOptions = useMemo(() => {
    const m = new Map();
    leads.forEach((l) => { if (l.assigned_to) m.set(l.assigned_to, l.assigned_to_name || "—"); });
    return Array.from(m, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [leads]);
  const sourceOptions = useMemo(() => {
    const set = new Set();
    leads.forEach((l) => { if (l.source_channel) set.add(l.source_channel); });
    return Array.from(set).sort();
  }, [leads]);
  const activeFilterCount = [fAdCopy, fCounsellor, fTemp, fSource, fSla, fDatePreset]
    .filter((v) => v !== "all").length;
  const clearFilters = () => {
    setFAdCopy("all"); setFCounsellor("all"); setFTemp("all");
    setFSource("all"); setFSla("all"); setFDatePreset("all");
    setFDateFrom(""); setFDateTo("");
  };

  const selectedIds = useMemo(
    () => Object.keys(selected).filter((k) => selected[k]),
    [selected],
  );

  const toggleSelect = (id) => setSelected((p) => ({ ...p, [id]: !p[id] }));

  const openBulkRefer = () => {
    if (selectedIds.length === 0) return;
    setReferIds(selectedIds);
    setReferOpen(true);
  };
  const openSingleRefer = (id) => {
    setReferIds([id]);
    setReferOpen(true);
  };
  const onReferDone = () => {
    setSelected({});
    setSelectedLead(null);
    fetchLeads();
  };

  const patchStatus = async (id, next, extra = {}) => {
    try {
      await api.patch(`/admin/leads/${id}`, { [sf]: next, ...extra });
      setLeads((prev) =>
        prev.map((l) => (l.id === id ? { ...l, [sf]: next, ...extra } : l)),
      );
      setSelectedLead((cur) =>
        cur && cur.id === id ? { ...cur, [sf]: next, ...extra } : cur,
      );
      toast.success(`Status → ${next}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // "Closed" → capture reason; "Referred" → open the Refer (teammate + note) flow.
  const requestStatusChange = (id, next) => {
    if (next === "Closed") {
      setPendingClose({ id });
    } else if (next === "Referred") {
      openSingleRefer(id);
    } else {
      patchStatus(id, next);
    }
  };

  const confirmClose = ({ reason, detail }) => {
    if (!pendingClose) return;
    patchStatus(pendingClose.id, "Closed", {
      close_reason: reason,
      close_reason_detail: detail || undefined,
    });
    setPendingClose(null);
  };

  const openSchedule = (lead) => {
    if (!lead) return;
    if (lead.scheduled_call_at) {
      const d = new Date(lead.scheduled_call_at);
      setScheduleDate(d.toISOString().slice(0, 10));
      setScheduleTime(d.toTimeString().slice(0, 5));
    } else {
      const t = new Date(Date.now() + 24 * 60 * 60 * 1000);
      setScheduleDate(t.toISOString().slice(0, 10));
      setScheduleTime("11:00");
    }
    setScheduleNote(lead.scheduled_call_note || "");
    setScheduleLead(lead);
  };

  const submitSchedule = async () => {
    if (!scheduleLead) return;
    if (!scheduleDate || !scheduleTime) {
      toast.error("Pick a date and time");
      return;
    }
    setScheduleSaving(true);
    try {
      const iso = new Date(`${scheduleDate}T${scheduleTime}:00`).toISOString();
      const { data } = await api.post(`/admin/leads/${scheduleLead.id}/schedule-call`, {
        scheduled_at: iso,
        note: scheduleNote,
      });
      setLeads((prev) => prev.map((l) => (l.id === data.lead.id ? data.lead : l)));
      setSelectedLead((cur) => (cur && cur.id === data.lead.id ? data.lead : cur));
      toast.success("Call scheduled");
      setScheduleLead(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setScheduleSaving(false);
    }
  };

  const onLeadEdited = (updated) => {
    setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
    setSelectedLead((cur) => (cur && cur.id === updated.id ? updated : cur));
    setEditLead(null);
  };

  const claimLead = async (id) => {
    try {
      const { data } = await api.post(`/admin/leads/${id}/claim`);
      setLeads((prev) => prev.map((l) => (l.id === id ? data.lead : l)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const releaseLead = async (id) => {
    try {
      const { data } = await api.post(`/admin/leads/${id}/release`);
      setLeads((prev) => prev.map((l) => (l.id === id ? data.lead : l)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const CHIPS = [
    { v: "all", l: "All", n: counts.all },
    ...config.chipStages.map((s) => ({ v: s, l: s, n: counts[s] })),
    ...(config.showRefByMe
      ? [{ v: "refbyme", l: "Ref by me", n: counts.refbyme }]
      : []),
  ];

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="leads-page">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 backdrop-blur-xl">
        <div className="flex h-16 items-center justify-between px-4 pl-16 pr-14 md:pl-6 md:pr-16">
          <h1 className="font-display flex items-center gap-2 text-2xl font-bold tracking-tight text-[#0A2540]">
            {config.pipeline === "counseling" && (
              <Flame className="h-5 w-5 text-orange-500" />
            )}
            {config.title}
          </h1>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setShowSearch((v) => !v)}
              className="flex h-9 w-9 items-center justify-center rounded-full text-slate-600 hover:bg-slate-100"
              data-testid="leads-search-toggle"
              aria-label="Search"
            >
              <Search className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => setShowFilters((v) => !v)}
              className={`relative flex h-9 items-center gap-1.5 rounded-full px-3 text-sm font-medium hover:bg-slate-100 ${showFilters || activeFilterCount > 0 ? "text-[#0A2540]" : "text-slate-600"}`}
              data-testid="leads-filters-toggle"
              aria-label="Filters"
            >
              <Filter className="h-5 w-5" />
              <span className="hidden sm:inline">Filters</span>
              {activeFilterCount > 0 && (
                <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-orange-500 px-1 text-[10px] font-bold text-white" data-testid="leads-filters-count">
                  {activeFilterCount}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={openBulkRefer}
              disabled={selectedIds.length === 0}
              className="flex h-9 items-center gap-1.5 rounded-full px-3 text-sm font-medium text-[#0A2540] hover:bg-[#C9A961]/15 disabled:opacity-40"
              data-testid="leads-refer-btn"
              aria-label="Refer selected leads"
            >
              <Share2 className="h-5 w-5" />
              <span className="hidden sm:inline">
                Refer{selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}
              </span>
            </button>
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              className="flex h-9 items-center gap-1.5 rounded-full bg-[#0A2540] px-3 text-sm font-medium text-white hover:bg-[#1E3A5F]"
              data-testid="leads-add-lead-btn"
              aria-label="Add lead"
            >
              <Plus className="h-5 w-5" />
              <span className="hidden sm:inline">Add lead</span>
            </button>
          </div>
        </div>

        {showSearch && (
          <div className="px-4 pb-3 md:px-6">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, phone, email…"
                className="h-10 pl-9"
                data-testid="leads-search-input"
              />
            </div>
          </div>
        )}

        {showFilters && (
          <div className="border-t border-slate-100 bg-slate-50/60 px-4 py-3 md:px-6" data-testid="leads-filter-panel">
            <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-6">
              <FilterSelect label="Ad copy" value={fAdCopy} onChange={setFAdCopy} testid="filter-ad-copy"
                options={[{ v: "all", l: "All ad copy" }, ...adCopyOptions.map((a) => ({ v: a, l: a }))]} />
              <FilterSelect label="Counsellor" value={fCounsellor} onChange={setFCounsellor} testid="filter-counsellor"
                options={[{ v: "all", l: "All counsellors" }, { v: "__unassigned__", l: "Unassigned" }, ...counsellorOptions.map((c) => ({ v: c.id, l: c.name }))]} />
              <FilterSelect label="Temperature" value={fTemp} onChange={setFTemp} testid="filter-temperature"
                options={[{ v: "all", l: "All temps" }, ...TEMP_OPTIONS.map((t) => ({ v: t, l: t }))]} />
              <FilterSelect label="Source" value={fSource} onChange={setFSource} testid="filter-source"
                options={[{ v: "all", l: "All sources" }, ...sourceOptions.map((s) => ({ v: s, l: s }))]} />
              <FilterSelect label="SLA" value={fSla} onChange={setFSla} testid="filter-sla"
                options={[{ v: "all", l: "All SLA" }, { v: "ontrack", l: "On track" }, { v: "overdue", l: "Overdue" }]} />
              <FilterSelect label="Created" value={fDatePreset} onChange={setFDatePreset} testid="filter-date-preset"
                options={DATE_PRESETS.map((d) => ({ v: d.v, l: d.l }))} />
            </div>
            {fDatePreset === "custom" && (
              <div className="mt-2 flex flex-wrap items-end gap-2">
                <div>
                  <Label className="text-[10px] uppercase tracking-wide text-slate-400">From</Label>
                  <Input type="date" value={fDateFrom} onChange={(e) => setFDateFrom(e.target.value)} className="h-9 w-[160px]" data-testid="filter-date-from" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wide text-slate-400">To</Label>
                  <Input type="date" value={fDateTo} onChange={(e) => setFDateTo(e.target.value)} className="h-9 w-[160px]" data-testid="filter-date-to" />
                </div>
              </div>
            )}
            {activeFilterCount > 0 && (
              <div className="mt-2 flex items-center justify-between">
                <span className="text-[11px] text-slate-500" data-testid="leads-active-filters">{activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"} applied</span>
                <Button variant="ghost" size="sm" onClick={clearFilters} className="h-7 text-xs text-slate-500 hover:text-rose-600" data-testid="leads-clear-filters">
                  <X className="mr-1 h-3.5 w-3.5" /> Clear all
                </Button>
              </div>
            )}
          </div>
        )}

        <div className="no-scrollbar flex gap-2 overflow-x-auto px-4 pb-3 md:px-6">
          {CHIPS.map((c) => (
            <button
              key={c.v}
              type="button"
              onClick={() => setStage(c.v)}
              data-testid={`stage-chip-${c.v}`}
              className={`shrink-0 whitespace-nowrap rounded-xl border px-4 py-2 text-sm font-medium transition-colors ${
                stage === c.v
                  ? "border-orange-500 bg-orange-500 text-white shadow-sm"
                  : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
              }`}
            >
              {c.l} ({c.n})
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl px-4 py-4 md:max-w-7xl md:px-6">
        <div className="mb-3 flex items-center justify-between text-sm">
          <span className="text-xs text-slate-500" data-testid="leads-count">
            {filtered.length} lead{filtered.length === 1 ? "" : "s"}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">Sort</span>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="leads-sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORTS.map((s) => (
                  <SelectItem key={s.v} value={s.v}>
                    {s.l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-sm text-slate-500">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">
            No leads in this view yet.
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div
              className="hidden overflow-x-auto rounded-xl border border-slate-200 bg-white md:block"
              data-testid="leads-table"
            >
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/80">
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Student</TableHead>
                    <TableHead>Actions</TableHead>
                    <TableHead>Policy</TableHead>
                    <TableHead>Lead Score</TableHead>
                    <TableHead>SLA</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last note</TableHead>
                    <TableHead className="text-right">Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.slice(0, 200).map((l) => {
                    const isMine = l.assigned_to === userId;
                    const st = l[sf];
                    const ro = l.out_of_scope;
                    return (
                      <TableRow
                        key={l.id}
                        data-testid={`lead-row-${l.id}`}
                        className={`cursor-pointer align-top hover:bg-slate-50/60 ${ro ? "bg-amber-50/40" : ""}`}
                        onClick={() => setSelectedLead(l)}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={!!selected[l.id]}
                            disabled={ro}
                            onCheckedChange={() => toggleSelect(l.id)}
                            data-testid={`lead-select-${l.id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span
                              className={`h-2 w-2 shrink-0 rounded-full ${
                                TEMP_DOT[l.lead_temperature] || "bg-slate-300"
                              }`}
                            />
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5 truncate text-sm font-semibold text-[#0A2540]">
                                {l.name || "—"}
                                {ro && (
                                  <span
                                    className="inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0 text-[10px] font-bold text-amber-700"
                                    data-testid={`lead-row-in-crm-${l.id}`}
                                    title={`Already in CRM — owned by ${l.assigned_to_name || "another member"}`}
                                  >
                                    In CRM
                                  </span>
                                )}
                                {waByLead?.[l.id] > 0 && (
                                  <span className="inline-flex items-center gap-0.5 rounded-full bg-[#25D366] px-1.5 py-0 text-[10px] font-bold text-white" data-testid={`lead-row-wa-unread-${l.id}`} title="Unread WhatsApp">
                                    <MessageCircle className="h-2.5 w-2.5" />{waByLead[l.id]}
                                  </span>
                                )}
                              </div>
                              <div className="truncate text-[11px] text-slate-500">
                                {l.phone || "—"}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <LeadContactActions lead={l} />
                        </TableCell>
                        <TableCell>
                          <ContactPolicyBadge lead={l} />
                          {(() => {
                            const p = policyFor(l);
                            if (p.kind !== "overdue") return null;
                            const sev = p.severity || "low";
                            const dot =
                              sev === "high"
                                ? "bg-rose-500"
                                : sev === "med"
                                  ? "bg-amber-500"
                                  : "bg-slate-400";
                            return (
                              <div
                                className="mt-1 flex max-w-[200px] items-start gap-1.5 text-[10.5px] leading-snug text-rose-700"
                                data-testid={`breach-reason-${l.id}`}
                                title={p.reason}
                              >
                                <span
                                  className={`mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full ${dot}`}
                                />
                                <span className="line-clamp-2">{p.reason}</span>
                              </div>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            {l.lead_temperature === "Hot" && (
                              <Flame className="h-3 w-3 text-rose-500" />
                            )}
                            <span className="text-xs text-slate-600">
                              {l.lead_temperature}
                            </span>
                            {typeof l.lead_score === "number" && (
                              <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
                                {l.lead_score}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {l.attempt_status && (
                            <AttemptCounter status={l.attempt_status} variant="compact" />
                          )}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {ro ? (
                            <div className="text-xs font-medium text-[#0A2540]" data-testid={`row-owner-${l.id}`}>
                              {l.assigned_to_name || "—"}
                            </div>
                          ) : l.assigned_to ? (
                            <div className="text-xs">
                              <div className="font-medium text-[#0A2540]">
                                {l.assigned_to_name || "—"}
                              </div>
                              {(isMine || user?.role === "admin") && (
                                <button
                                  type="button"
                                  onClick={() => releaseLead(l.id)}
                                  className="text-[10px] text-slate-400 hover:text-rose-600"
                                  data-testid={`row-release-${l.id}`}
                                >
                                  release
                                </button>
                              )}
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={() => claimLead(l.id)}
                              data-testid={`row-claim-${l.id}`}
                            >
                              Claim
                            </Button>
                          )}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {ro ? (
                            <span
                              className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${config.statusPill[st] || "bg-slate-100 text-slate-600"}`}
                              data-testid={`row-status-readonly-${l.id}`}
                            >
                              {st || config.defaultStatus}
                            </span>
                          ) : (
                            <Select
                              value={config.stages.includes(st) ? st : undefined}
                              onValueChange={(v) => requestStatusChange(l.id, v)}
                            >
                              <SelectTrigger
                                className={`h-7 w-[150px] text-xs ${config.statusPill[st] || ""}`}
                                data-testid={`row-status-${l.id}`}
                              >
                                <SelectValue placeholder={st || config.defaultStatus} />
                              </SelectTrigger>
                              <SelectContent>
                                {config.stages.map((s) => (
                                  <SelectItem key={s} value={s}>
                                    {s}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </TableCell>
                        <TableCell className="max-w-[200px]">
                          {l.latest_note ? (
                            <div
                              className="line-clamp-2 text-xs text-slate-700"
                              title={l.latest_note.preview}
                            >
                              {l.latest_note.preview}
                            </div>
                          ) : l.notes ? (
                            <div
                              className="line-clamp-2 text-xs italic text-slate-500"
                              title={l.notes}
                            >
                              {l.notes}
                            </div>
                          ) : (
                            <span className="text-xs italic text-slate-300">—</span>
                          )}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-right text-xs text-slate-500">
                          {fmtDate(l.created_at)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Mobile cards */}
            <ul className="space-y-2 md:hidden" data-testid="leads-list">
              {filtered.slice(0, 200).map((l) => {
                const st = l[sf];
                const ro = l.out_of_scope;
                return (
                  <li
                    key={l.id}
                    data-testid={`lead-card-${l.id}`}
                    className={`rounded-xl border p-3 shadow-sm ${ro ? "border-amber-200 bg-amber-50/40" : "border-slate-200 bg-white"}`}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={!!selected[l.id]}
                        disabled={ro}
                        onCheckedChange={() => toggleSelect(l.id)}
                        className="mt-1.5"
                        data-testid={`lead-select-${l.id}`}
                      />
                      <button
                        type="button"
                        className="flex min-w-0 flex-1 items-start gap-3 text-left"
                        onClick={() => setSelectedLead(l)}
                      >
                        <span
                          className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                            TEMP_DOT[l.lead_temperature] || "bg-slate-300"
                          }`}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-sm font-semibold text-[#0A2540]">
                              {l.name || "—"}
                            </span>
                            {ro && (
                              <span
                                className="inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0 text-[10px] font-bold text-amber-700"
                                data-testid={`lead-card-in-crm-${l.id}`}
                                title={`Already in CRM — owned by ${l.assigned_to_name || "another member"}`}
                              >
                                In CRM
                              </span>
                            )}
                            {waByLead?.[l.id] > 0 && (
                              <span className="inline-flex items-center gap-0.5 rounded-full bg-[#25D366] px-1.5 py-0 text-[10px] font-bold text-white" data-testid={`lead-wa-unread-${l.id}`} title="Unread WhatsApp">
                                <MessageCircle className="h-2.5 w-2.5" />{waByLead[l.id]}
                              </span>
                            )}
                            {l.lead_temperature === "Hot" && (
                              <Flame className="h-3 w-3 shrink-0 text-rose-500" />
                            )}
                            {typeof l.lead_score === "number" && (
                              <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
                                {l.lead_score}
                              </Badge>
                            )}
                          </div>
                          <div className="mt-0.5 truncate text-[11px] text-slate-500">
                            {l.phone || "—"}
                            {l.assigned_to_name ? ` · ${l.assigned_to_name}` : ""}
                          </div>
                          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                            <span
                              className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                                config.statusPill[st] || "bg-slate-100 text-slate-600"
                              }`}
                            >
                              {st || config.defaultStatus}
                            </span>
                            {l.attempt_status && (
                              <AttemptCounter status={l.attempt_status} variant="compact" />
                            )}
                            <ContactPolicyBadge lead={l} />
                          </div>
                        </div>
                        <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-slate-300" />
                      </button>
                    </div>

                    <div className="mt-2.5 flex items-center gap-2 border-t border-slate-100 pt-2.5">
                      {l.phone && (
                        <a
                          href={`tel:${String(l.phone).replace(/\D/g, "")}`}
                          data-testid={`lead-call-${l.id}`}
                          className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
                        >
                          <Phone className="h-3.5 w-3.5" /> Call
                        </a>
                      )}
                      {l.phone && (
                        <a
                          href={waUrl(l.phone, l.name)}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`lead-wa-${l.id}`}
                          className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-[#25D366]/10 px-3 py-2 text-xs font-medium text-[#128C7E] hover:bg-[#25D366]/20"
                        >
                          <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                        </a>
                      )}
                      <button
                        type="button"
                        onClick={() => openSchedule(l)}
                        data-testid={`lead-schedule-${l.id}`}
                        className={`flex items-center justify-center gap-1.5 rounded-md bg-blue-50 px-3 py-2 text-xs font-medium text-blue-700 hover:bg-blue-100 ${ro ? "hidden" : ""}`}
                      >
                        <CalendarClock className="h-3.5 w-3.5" /> Schedule
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </main>

      {selectedIds.length > 0 && (
        <div
          className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white p-3 shadow-lg"
          data-testid="bulk-bar"
        >
          <div className="mx-auto flex max-w-3xl items-center justify-between gap-3">
            <span className="text-sm font-medium text-[#0A2540]">
              {selectedIds.length} selected
            </span>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setSelected({})} data-testid="bulk-clear">
                <X className="mr-1 h-4 w-4" /> Clear
              </Button>
              <Button
                size="sm"
                onClick={openBulkRefer}
                className="bg-[#0A2540] hover:bg-[#0A2540]/90"
                data-testid="bulk-refer"
              >
                <Share2 className="mr-1.5 h-4 w-4" /> Refer leads
              </Button>
            </div>
          </div>
        </div>
      )}

      <ReferLeadsDialog
        open={referOpen}
        onOpenChange={setReferOpen}
        leadIds={referIds}
        currentUserId={userId}
        toCounseling={config.referToCounseling}
        onDone={onReferDone}
      />

      <CloseReasonDialog
        open={!!pendingClose}
        onOpenChange={(o) => !o && setPendingClose(null)}
        onConfirm={confirmClose}
      />

      <LeadDrawer
        lead={selectedLead}
        config={config}
        onClose={() => setSelectedLead(null)}
        onRefer={(id) => openSingleRefer(id)}
        onStatusChange={requestStatusChange}
        onChanged={fetchLeads}
        onSchedule={(lead) => openSchedule(lead)}
        onEdit={(lead) => setEditLead(lead)}
      />

      {/* Schedule-call dialog */}
      <Dialog open={!!scheduleLead} onOpenChange={(o) => !o && setScheduleLead(null)}>
        <DialogContent className="sm:max-w-md" data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Schedule call</DialogTitle>
            <DialogDescription>
              {scheduleLead ? `${scheduleLead.name || "Lead"} · ${scheduleLead.phone || ""}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">Date</Label>
                <Input
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  min={new Date().toISOString().slice(0, 10)}
                  className="mt-1.5 h-11"
                  data-testid="schedule-date"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">Time</Label>
                <Input
                  type="time"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  className="mt-1.5 h-11"
                  data-testid="schedule-time"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">
                Note (optional)
              </Label>
              <Textarea
                rows={3}
                placeholder="Talking points, language preference, etc."
                value={scheduleNote}
                onChange={(e) => setScheduleNote(e.target.value)}
                className="mt-1.5"
                data-testid="schedule-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleLead(null)}>
              Cancel
            </Button>
            <Button
              onClick={submitSchedule}
              disabled={scheduleSaving}
              className="bg-[#0A2540] text-white hover:bg-[#1E3A5F]"
              data-testid="schedule-confirm"
            >
              {scheduleSaving ? "Saving…" : "Confirm schedule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit lead name / phone / email */}
      {editLead && (
        <EditLeadDetailsModal
          lead={editLead}
          open={!!editLead}
          onClose={() => setEditLead(null)}
          onSaved={onLeadEdited}
        />
      )}

      <AddLeadDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        pipeline={config.pipeline}
        onCreated={() => fetchLeads()}
      />
    </div>
  );
}

/* ---------------- Lead detail drawer ---------------- */
function LeadDrawer({ lead, config, onClose, onRefer, onStatusChange, onChanged, onSchedule, onEdit }) {
  const sf = config.statusField;
  const { user } = useAuth();
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [notesKey, setNotesKey] = useState(0);
  const [local, setLocal] = useState(lead);
  const [transferOpen, setTransferOpen] = useState(false);

  useEffect(() => {
    setLocal(lead);
    setNote("");
  }, [lead]);

  if (!lead) return null;

  const claim = async () => {
    try {
      const { data } = await api.post(`/admin/leads/${lead.id}/claim`);
      setLocal(data.lead);
      toast.success("Claimed");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const release = async () => {
    try {
      const { data } = await api.post(`/admin/leads/${lead.id}/release`);
      setLocal(data.lead);
      toast.success("Released");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const saveNote = async () => {
    if (!note.trim()) return;
    setSavingNote(true);
    try {
      await api.post(`/admin/leads/${lead.id}/notes`, { content: note.trim() });
      setNote("");
      setNotesKey((k) => k + 1);
      toast.success("Note added");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingNote(false);
    }
  };

  const owned = local?.assigned_to;
  const isMine = owned === user?.id;
  const curStatus = local?.[sf];
  const readOnly = !!local?.out_of_scope;

  return (
    <Sheet open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-md" data-testid="lead-drawer">
        <SheetHeader className="text-left">
          <SheetTitle className="flex items-center gap-2 text-[#0A2540]">
            <span className="truncate">{local?.name || "Lead"}</span>
            {!readOnly && (
              <button
                type="button"
                onClick={() => onEdit?.(local)}
                data-testid="drawer-edit-details"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-[#0A2540]"
                aria-label="Edit lead details"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
          </SheetTitle>
          <SheetDescription className="text-xs">
            {readOnly
              ? "This lead already exists in your consultancy. View-only."
              : "Manage stage, ownership, outreach and notes for this lead."}
          </SheetDescription>
        </SheetHeader>

        {readOnly && (
          <div
            className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800"
            data-testid="drawer-readonly-banner"
          >
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Already in CRM — owned by{" "}
              <span className="font-semibold">{local?.assigned_to_name || "another member"}</span>.
              You have view-only access.
            </span>
          </div>
        )}

        <div className="mt-4 space-y-5 pb-10">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <Field label="Phone" value={local?.phone} />
            <Field label="Email" value={local?.email} />
            <Field label="Source" value={local?.source_channel} />
            <Field label="Temperature" value={local?.lead_temperature} />
            <Field label="Owner" value={local?.assigned_to_name || "Unassigned"} />
            <Field label="Score" value={local?.lead_score} />
            {curStatus === "Closed" && local?.close_reason && (
              <Field
                label="Close reason"
                value={
                  local.close_reason === "Other" && local.close_reason_detail
                    ? `Other — ${local.close_reason_detail}`
                    : local.close_reason
                }
              />
            )}
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-600">Stage</label>
            {readOnly ? (
              <span
                className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${config.statusPill[curStatus] || "bg-slate-100 text-slate-600"}`}
                data-testid="drawer-status-readonly"
              >
                {curStatus || config.defaultStatus}
              </span>
            ) : (
              <Select
                value={config.stages.includes(curStatus) ? curStatus : undefined}
                onValueChange={(v) => onStatusChange(lead.id, v)}
              >
                <SelectTrigger data-testid="drawer-status-select">
                  <SelectValue placeholder={curStatus || config.defaultStatus} />
                </SelectTrigger>
                <SelectContent>
                  {config.stages.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`drawer-status-${s}`}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {!readOnly && (
          <div className="flex flex-wrap items-center gap-2">
            {!owned ? (
              <Button size="sm" variant="outline" onClick={claim} data-testid="drawer-claim">
                Claim
              </Button>
            ) : isMine || user?.role === "admin" ? (
              <Button size="sm" variant="outline" onClick={release} data-testid="drawer-release">
                Release
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="outline"
              onClick={() => onRefer(lead.id)}
              data-testid="drawer-refer"
            >
              <Share2 className="mr-1.5 h-4 w-4" /> Refer
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onSchedule?.(local)}
              data-testid="drawer-schedule-call"
            >
              <CalendarClock className="mr-1.5 h-4 w-4" />
              {local?.scheduled_call_at ? "Reschedule" : "Schedule"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setTransferOpen(true)}
              data-testid="drawer-transfer"
            >
              <ArrowRightLeft className="mr-1.5 h-4 w-4" /> Transfer
            </Button>
            <div className="ml-auto">
              <LeadContactActions lead={local} />
            </div>
          </div>
          )}

          {local?.scheduled_call_at && (
            <div
              className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50/60 p-2.5 text-xs text-blue-900"
              data-testid="drawer-scheduled-pill"
            >
              <CalendarClock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <div>
                <div className="font-medium">Call: {fmtDate(local.scheduled_call_at)}</div>
                {local.scheduled_call_note && (
                  <div className="text-blue-700/80">{local.scheduled_call_note}</div>
                )}
              </div>
            </div>
          )}

          {local?.attempt_status && (
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
              <div className="mb-2 text-xs font-medium text-slate-600">Outreach SLA</div>
              <AttemptCounter status={local.attempt_status} variant="full" />
            </div>
          )}

          {!readOnly && (
          <>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-600">Add note</label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Log a note…"
              rows={2}
              data-testid="drawer-note-input"
            />
            <div className="mt-2 flex items-center gap-2">
              <Button
                size="sm"
                onClick={saveNote}
                disabled={savingNote || !note.trim()}
                data-testid="drawer-note-save"
                className="bg-[#0A2540] hover:bg-[#0A2540]/90"
              >
                {savingNote && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save note
              </Button>
              <VoiceNoteRecorder
                leadId={lead.id}
                compact
                onTranscribed={() => setNotesKey((k) => k + 1)}
              />
            </div>
            <NotesTimeline leadId={lead.id} refreshKey={notesKey} />
          </div>

          <div className="rounded-lg border border-slate-100 bg-white p-3">
            <LeadDocumentsPanel leadId={lead.id} />
          </div>

          <div className="rounded-lg border border-slate-100 bg-white p-3">
            <LeadPassesPanel leadId={lead.id} />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-600">
              WhatsApp (MyOperator)
            </label>
            <p className="mb-2 text-[11px] text-slate-400">
              Inbound &amp; outbound messages. Free-form replies allowed within the
              24-hour window after a lead messages you.
            </p>
            <WhatsAppConversation leadId={lead.id} leadName={local?.name} />
          </div>
          </>
          )}
        </div>
      </SheetContent>
      <TransferLeadDialog
        lead={local}
        open={transferOpen}
        onOpenChange={setTransferOpen}
        onTransferred={() => {
          onClose();
          onChanged?.();
        }}
      />
    </Sheet>
  );
}

function Field({ label, value }) {
  return (
    <div className="rounded-md border border-slate-100 bg-slate-50/50 px-2.5 py-1.5">
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="truncate text-sm text-[#0A2540]">{value ?? "—"}</div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wide text-slate-400">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-0.5 h-9 text-xs" data-testid={testid}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.v} value={o.v} className="text-xs">{o.l}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
